import React, { useEffect, useState } from 'react';
import { API, authHeaders } from '../utils/helpers';

// ── Request estimate (26 Oct) ────────────────────────────────────────────────
//
// A tech tells a Manager or Admin that this customer needs an estimate, and
// what they found (server/helpers/estimateRequests.js). Used on the bench and
// in the ticket window. The banner shows an open request on the ticket to
// everyone; a Manager or Admin can dismiss it with a reason. Sending the
// estimate closes it by itself.

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders(!!opts.body), ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
  return d;
}

const stamp = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (_) { return ''; } };

export function RequestEstimateDialog({ ticketId, T, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const send = async () => {
    setBusy(true); setError('');
    try { onDone(await api('/api/quotes/estimate-requests', { method: 'POST', body: JSON.stringify({ workorderId: ticketId, note }) })); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div role="dialog" aria-modal="true" aria-label="Request an estimate" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 460, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>Request an estimate</div>
        <div style={{ fontSize: 12.5, color: T.textSub, lineHeight: 1.5, marginBottom: 12 }}>
          A Manager or Admin sees it in Quotes and sends the customer the estimate. Add the parts and labor to the ticket so the estimate has them.
        </div>
        <label style={{ display: 'block', fontSize: 12, color: T.textSub, marginBottom: 12 }}>What did you find?
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={4} autoFocus
            placeholder="e.g. Rear shock leaking oil, needs a full rebuild. Front fork is fine."
            style={{ width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '9px 11px', fontSize: 14, borderRadius: 6, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit', resize: 'vertical' }} />
        </label>
        {error && <div style={{ fontSize: 12.5, color: '#e74c3c', marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy}
            style={{ padding: '10px 16px', borderRadius: 8, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Cancel</button>
          <button onClick={send} disabled={busy || !note.trim()}
            style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: '#e67e22', color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: busy || !note.trim() ? 'default' : 'pointer', opacity: busy || !note.trim() ? 0.6 : 1 }}>
            {busy ? 'Sending…' : 'Request estimate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// The ticket's open request, for everyone who opens the ticket.
export function EstimateRequestBanner({ ticketId, T, canDismiss = false, refreshKey = 0, onSendEstimate = null }) {
  const [req, setReq] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setReq(null);
    api(`/api/quotes/estimate-requests/for-ticket/${encodeURIComponent(ticketId)}`).then(d => { if (live) setReq(d.request); }).catch(() => {});
    return () => { live = false; };
  }, [ticketId, refreshKey]);
  if (!req || req.status !== 'open') return null;
  const dismiss = async () => {
    setError('');
    try { const d = await api(`/api/quotes/estimate-requests/${req.id}/dismiss`, { method: 'POST', body: JSON.stringify({ reason }) }); setReq(d.request); window.dispatchEvent(new Event('spinboard-quote-answer-seen')); }
    catch (e) { setError(e.message); }
  };
  return (
    <div style={{ marginTop: 14, border: '1px solid rgba(230,126,34,0.55)', background: 'rgba(230,126,34,0.08)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#e67e22', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Needs an estimate</div>
      <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{req.note || 'No note.'}</div>
      <div style={{ fontSize: 11.5, color: T.textSub, marginTop: 4 }}>
        Requested by {req.requestedBy || 'staff'}, {stamp(req.requestedAt)}{req.statusNote ? ` · ${req.statusNote}` : ''}
      </div>
      {(onSendEstimate || canDismiss) && !dismissing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {onSendEstimate && <button onClick={onSendEstimate} style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: '#e67e22', color: '#fff', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>Send estimate</button>}
          {canDismiss && <button onClick={() => setDismissing(true)} style={{ padding: '7px 14px', borderRadius: 7, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Dismiss</button>}
        </div>
      )}
      {dismissing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why? e.g. Called the customer instead" autoFocus
            style={{ flex: 1, minWidth: 200, padding: '7px 10px', fontSize: 13, borderRadius: 6, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit' }} />
          <button onClick={dismiss} disabled={!reason.trim()} style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: T.text, color: T.card || '#fff', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', opacity: reason.trim() ? 1 : 0.5 }}>Dismiss request</button>
          <button onClick={() => { setDismissing(false); setError(''); }} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit' }}>Cancel</button>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: '#e74c3c', marginTop: 6 }}>{error}</div>}
    </div>
  );
}
